import { type FormEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type FormFieldType = 'text' | 'email' | 'password' | 'number';

export interface FormField {
  name: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}

export interface FormBuilderProps {
  fields: ReadonlyArray<FormField>;
  values: Readonly<Record<string, string>>;
  onChange: (name: string, value: string) => void;
  onSubmit: (values: Readonly<Record<string, string>>) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  footer?: ReactNode;
}

/**
 * FormBuilder
 *
 * 受控表单封装。表单不会自行调用任何 API，提交动作必须通过 `onSubmit`
 * 由调用方使用真实的 typed client 发起。
 *
 * 后续 Issue 会基于 React Hook Form + Zod 提供更强类型校验，
 * 此组件作为骨架以减少业务页面重复代码。
 */
export function FormBuilder({
  fields,
  values,
  onChange,
  onSubmit,
  isSubmitting = false,
  submitLabel = '提交',
  footer,
}: FormBuilderProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {fields.map((field) => (
        <div key={field.name} className="space-y-1.5">
          <Label htmlFor={field.name}>
            {field.label}
            {field.required ? <span className="ml-1 text-destructive">*</span> : null}
          </Label>
          <Input
            id={field.name}
            name={field.name}
            type={field.type}
            required={field.required}
            placeholder={field.placeholder}
            autoComplete={field.autoComplete}
            value={values[field.name] ?? ''}
            disabled={isSubmitting}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
        </div>
      ))}

      <div className="flex items-center justify-between gap-3">
        {footer ?? <span />}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? '提交中…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
